/**
 * Orchestra AI Platform - Project Manager Component
 * 
 * Component for managing projects: list, create, archive, organize
 * 
 * @fileoverview Project manager with CRUD operations and filtering
 */

import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ProjectCard } from './ProjectCard';
import { CreateProjectDialog } from './CreateProjectDialog';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import './Workspace.css';

/**
 * @typedef {Object} Project
 * @property {string} id
 * @property {string} user_id
 * @property {string} name
 * @property {string} description
 * @property {('workflow'|'api_integration'|'cross_subject')} type
 * @property {('active'|'archived'|'template')} status
 * @property {Object} settings
 * @property {string} created_at
 * @property {string} updated_at
 * @property {string} last_opened_at
 */

/**
 * Project Manager Component
 * @param {Object} props
 * @param {string} [props.searchQuery] - Search query
 * @param {string} [props.filterType] - Filter by project type
 * @param {boolean} [props.archived] - Show archived projects
 * @param {Function} [props.onProjectSelect] - Handler for project selection
 */
export function ProjectManager({ searchQuery = '', filterType = 'all', archived = false, onProjectSelect }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [user, setUser] = useState(null);

  // Get current user
  useEffect(() => {
    getCurrentUser();
  }, []);

  // Load projects
  useEffect(() => {
    if (user) {
      loadProjects();
    }
  }, [user, archived, filterType, searchQuery]);

  // Listen for project creation events to refresh list
  useEffect(() => {
    if (!user) return;
    
    const handleProjectCreated = (event) => {
      console.log('[Project Manager] Project created event received:', event.detail);
      // Refresh projects list after a short delay to ensure DB is updated
      setTimeout(() => {
        loadProjects();
      }, 500);
    };

    window.addEventListener('projectCreated', handleProjectCreated);
    return () => {
      window.removeEventListener('projectCreated', handleProjectCreated);
    };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Get current user
   */
  const getCurrentUser = async () => {
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      setUser(currentUser);
    } catch (error) {
      console.error('Failed to get user:', error);
    }
  };

  /**
   * Load projects from database
   */
  const loadProjects = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('developer_projects')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', archived ? 'archived' : 'active');

      // Apply type filter
      if (filterType !== 'all') {
        query = query.eq('type', filterType);
      }

      // Apply search
      if (searchQuery) {
        query = query.or(`name.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`);
      }

      const { data, error } = await query.order('last_opened_at', { ascending: false });

      if (error) throw error;
      setProjects(data || []);
    } catch (error) {
      toast.error('Failed to load projects: ' + error.message);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Create new project
   * @param {Partial<Project>} projectData
   */
  const createProject = async (projectData) => {
    try {
      const { data, error } = await supabase
        .from('developer_projects')
        .insert({
          user_id: user.id,
          name: projectData.name,
          description: projectData.description || '',
          type: projectData.type || 'workflow',
          status: 'active',
          settings: {
            auto_save: true,
            collaboration_enabled: false,
            default_timeout: 30000,
          },
        })
        .select()
        .single();

      if (error) throw error;

      toast.success('Project created successfully');
      setShowCreateDialog(false);
      
      // Navigate to project
      if (onProjectSelect) {
        onProjectSelect(data.id);
      }
    } catch (error) {
      toast.error('Failed to create project: ' + error.message);
    }
  };

  /**
   * Archive project
   * @param {string} projectId
   */
  const archiveProject = async (projectId) => {
    try {
      const { error } = await supabase
        .from('developer_projects')
        .update({ status: 'archived' })
        .eq('id', projectId);

      if (error) throw error;

      toast.success('Project archived');
      loadProjects();
    } catch (error) {
      toast.error('Failed to archive project: ' + error.message);
    }
  };

  /**
   * Delete project
   * @param {string} projectId
   */
  const deleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('developer_projects')
        .delete()
        .eq('id', projectId);

      if (error) throw error;

      toast.success('Project deleted');
      loadProjects();
    } catch (error) {
      toast.error('Failed to delete project: ' + error.message);
    }
  };

  /**
   * Update last opened time
   * @param {string} projectId
   */
  const updateLastOpened = async (projectId) => {
    try {
      await supabase
        .from('developer_projects')
        .update({ last_opened_at: new Date().toISOString() })
        .eq('id', projectId);
    } catch (error) {
      console.error('Failed to update last opened:', error);
    }
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div>Loading projects...</div>
      </div>
    );
  }

  return (
    <div className="project-manager">
      {/* Projects Grid */}
      {projects.length === 0 ? (
        <div className="empty-state">
          <p>
            {archived ? 'No archived projects' : 'No projects yet'}
          </p>
          {!archived && (
            <button
              onClick={() => setShowCreateDialog(true)}
              className="empty-state-button"
            >
              Create Your First Project
            </button>
          )}
        </div>
      ) : (
        <div className="projects-grid">
          {projects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onOpen={() => {
                updateLastOpened(project.id);
                if (onProjectSelect) {
                  onProjectSelect(project.id);
                }
              }}
              onArchive={() => archiveProject(project.id)}
              onDelete={() => deleteProject(project.id)}
            />
          ))}
        </div>
      )}

      {/* Create Project Dialog */}
      {showCreateDialog && (
        <CreateProjectDialog
          onSave={createProject}
          onCancel={() => setShowCreateDialog(false)}
        />
      )}
    </div>
  );
}

ProjectManager.propTypes = {
  searchQuery: PropTypes.string,
  filterType: PropTypes.string,
  archived: PropTypes.bool,
};

